import { ADMISSION_TYPE_BOTTLE_SERVICE, ADMISSION_TYPE_GUEST_LIST } from '@domain/admission';
import { BookingFormField } from '@domain/booking-form-field';
import { Company } from '@domain/company';
import { Event } from '@domain/event';
import { FormFieldPreset } from '@domain/form-field-presets';

import { filterNewOfferFormFields, mergeFormFields } from './format-ticket-formFields-from-backend';
import { FIELD_EMAIL, FIELD_PHONE } from './validate-import-csv';

// Interface for SIAE validation - supports both Admission and Pass types
interface OfferWithSiaeSettings {
  settings?: {
    italyRegulation?: {
      isSiaeApproved?: boolean;
      isSiaeExcluded?: boolean;
    };
  };
}

interface FormatBookingHolderProps {
  formFields: FormFieldPreset[];
  type?: string;
  isEdit?: boolean;
  isFormFieldDisabled?: boolean;
  includeMinMax?: boolean;
  offerBookingFormFields?: BookingFormField[];
  event?: Event;
  offer?: OfferWithSiaeSettings | null;
  company?: Company;
}

/**
 * Determines if the phone field should be required and disabled based on SIAE regulations.
 *
 * @param event - The event object
 * @param offer - The offer object (admission or pass)
 * @param company - The company object
 * @param type - The admission/offer type (e.g., 'guestList', 'ticket', 'bottleService')
 * @returns true if any of these conditions are met:
 * 1. Event is SIAE: event.settings.italyRegulation.isSIAE = true
 * 2. Event is not SIAE but offer is SIAE approved: offer.settings.italyRegulation.isSiaeApproved = true
 * 3. Offer is not SIAE approved but is not excluded: offer.settings.italyRegulation.isSiaeExcluded = false
 * 4. For presets (no event or offer): company.siaeApproved = true
 *
 * IMPORTANT: SIAE logic does NOT apply to guestList admissions
 */
export const shouldPhoneBeRequiredForSiae = (
  event?: Event,
  offer?: OfferWithSiaeSettings | null,
  company?: Company,
  type?: string
): boolean => {
  // SIAE logic does NOT apply to guestList admissions
  if (type === ADMISSION_TYPE_GUEST_LIST) return false;

  if (event?.settings.italyRegulation.isSIAE) return true;
  if (offer?.settings?.italyRegulation?.isSiaeApproved) return true;
  if (offer?.settings?.italyRegulation?.isSiaeExcluded === false) return true;

  // For presets: when there's no event or offer, check company's siaeApproved flag
  if (!event && !offer && company?.siaeApproved) return true;

  return false;
};

export const formFieldPresetToFormField = ({
  formFields,
  type = '',
  isEdit = false,
  isFormFieldDisabled = false,
  includeMinMax = false,
  event,
  offer,
  company,
}: Omit<FormatBookingHolderProps, 'offerBookingFormFields'>) => {
  const isPhoneRequiredForSiae = shouldPhoneBeRequiredForSiae(event, offer, company, type);

  return formFields.map(({ name, slug, dataType, isAssignedByDefault, min, max }) => {
    const isPhoneField = slug === FIELD_PHONE;

    // Determine if phone field should be required based on existing logic or SIAE
    const isPhoneRequired =
      (type === ADMISSION_TYPE_BOTTLE_SERVICE && isPhoneField && !isEdit) || (isPhoneField && isPhoneRequiredForSiae);

    // Determine if phone field should be disabled based on SIAE or other conditions
    const isPhoneDisabled = isPhoneField && isPhoneRequiredForSiae;

    const baseField = {
      name,
      dataType,
      isRequired: isPhoneRequired || isAssignedByDefault,
      disabled: slug === FIELD_EMAIL || isFormFieldDisabled || isPhoneDisabled,
    };

    if (includeMinMax) {
      return {
        ...baseField,
        min,
        max,
      };
    }

    return baseField;
  });
};

export const formatBookingHolderFromBackend = ({
  formFields,
  offerBookingFormFields,
  type = '',
  isEdit = false,
  isFormFieldDisabled = false,
  includeMinMax = false,
  event,
  offer,
  company,
}: FormatBookingHolderProps) => {
  const initialBookingformFields = formFieldPresetToFormField({
    formFields,
    type,
    isFormFieldDisabled,
    isEdit,
    includeMinMax,
    event,
    offer,
    company,
  });

  const bookingFormFields = mergeFormFields(initialBookingformFields, offerBookingFormFields);

  const newBookingFormFields = filterNewOfferFormFields({
    offerFormFields: offerBookingFormFields,
    initialFormFields: bookingFormFields,
    includeMinMax,
    isFormFieldDisabled,
  });

  return [...bookingFormFields, ...newBookingFormFields];
};
